import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { renewLookoutSession } from '@/auth/lib/shared-session';

import { clearAccountDrafts } from '../lib/account-drafts';
import { orpc } from '../lib/orpc';
import { useAuth } from './useAuth';

const NEW_WORKSPACE_PATH = '/new-workspace';

interface RudderstackReauth {
  /** The provider probe is still loading, so the path is not known yet. */
  isLoading: boolean;
  /** Lookout's own sign-in is on: re-auth is a sign-out plus `/login`, never the web app. */
  native: boolean;
  /** The OAuth start, for the anchor shown when `native` is false and the probe has answered. */
  oauthUrl: string;
  /** True while signing out, and after, while the browser leaves. */
  pending: boolean;
  start: () => void;
}

export interface RudderstackReauthOptions {
  /**
   * Where the person comes back to: a path, with its search. Defaults to /new-workspace. A sign-in in place (ACT2-1124)
   * passes the current page, so a renewal from the shared session keeps the person where they are.
   */
  returnTo?: string;
  /** Called after Lookout's session renewed from the shared session, with no sign-in. */
  onRenewed?: () => void;
}

/**
 * A fresh RudderStack sign-in that comes back to `returnTo` (/new-workspace by default). With the shared session
 * (ACT2-879), Lookout's session first renews from it: the user signs in again only when the shared session ended.
 * After a renewal, the page opens `returnTo` when it is another page, as a new sign-in does.
 */
export function useRudderstackReauth(options: RudderstackReauthOptions = {}): RudderstackReauth {
  const returnTo = options.returnTo ?? NEW_WORKSPACE_PATH;
  // A slash is legal in a query value; keeping it keeps the default URL readable.
  const returnParam = encodeURIComponent(returnTo).replaceAll('%2F', '/');
  const oauthUrl = `/auth/rudderstack?return_to=${returnParam}`;
  const nativeUrl = `/login?return_to=${returnParam}`;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { data, isLoading } = useQuery(orpc.auth.providers.queryOptions());
  const { signOutAndRedirect, user } = useAuth();
  const native = Boolean(data?.nativeSignIn);
  const sharedApiBaseUrl = data?.nativeSignIn?.sharedSession ? data.nativeSignIn.apiBaseUrl : null;
  // With Lookout's own sign-in the OAuth start only returns to /login; the same person signs back in, so the provider session stays.
  const nativeReauth = useMutation({
    mutationFn: async (): Promise<'renewed' | 'left'> => {
      if (sharedApiBaseUrl) {
        const outcome = await renewLookoutSession(sharedApiBaseUrl, { force: true, userId: user?.id });
        if (outcome === 'switched') {
          // Another user signed in on the web app: a full load shows that user's data only.
          clearAccountDrafts();
          window.location.assign('/');
          return 'left';
        }
        if (outcome === 'renewed' || outcome === 'current') {
          await queryClient.invalidateQueries();
          // `returnTo` may carry a query string, so compare the whole location.
          if (`${pathname}${search}` !== returnTo) void navigate(returnTo);
          options.onRenewed?.();
          return 'renewed';
        }
        if (outcome === 'unavailable') {
          toast.error('Could not reach RudderStack. Try again.');
          throw new Error('The shared session did not answer.');
        }
      }
      const left = await signOutAndRedirect(nativeUrl, {
        keepProviderSession: true,
        // An ended shared session needs no sign-out at config-backend.
        keepSharedSession: sharedApiBaseUrl !== null,
      });
      if (!left) throw new Error('Could not sign you out.');
      return 'left';
    },
  });

  return {
    isLoading,
    native,
    oauthUrl,
    pending: nativeReauth.isPending || nativeReauth.data === 'left',
    start: () => {
      // Until the probe answers, the right path is not known.
      if (isLoading) return;
      if (native) nativeReauth.mutate();
      else window.location.assign(oauthUrl);
    },
  };
}
